import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { TenantConfig, TenantContextType, TenantBranding } from '../types/tenant';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.archiveabyss.com';

const DEFAULT_BRANDING: TenantBranding = {
  tenant_id: '',
  company_name: 'ArchiveAbyss',
  primary_color: '#7c3aed',
  accent_color: '#a78bfa',
  background_color: '#0a0a0f',
  logo_url: '',
  favicon_url: '',
};

const TenantContext = createContext<TenantContextType>({
  config: null,
  loading: true,
  error: null,
  subdomain: null,
});

function getSubdomainFromHost(): string | null {
  const hostname = window.location.hostname;

  // Handle localhost/dev environments
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check URL params for testing: ?tenant=tlg
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant');
  }

  // Handle archiveabyss.com domains
  if (hostname.endsWith('.archiveabyss.com')) {
    const parts = hostname.split('.');
    // e.g., tlg.archiveabyss.com -> ['tlg', 'archiveabyss', 'com']
    if (parts.length === 3 && parts[0] !== 'www' && parts[0] !== 'api') {
      return parts[0];
    }
  }

  return null;
}

function applyBranding(branding: TenantBranding) {
  const root = document.documentElement;

  // Apply CSS custom properties
  root.style.setProperty('--accent', branding.primary_color);
  root.style.setProperty('--accent-secondary', branding.accent_color);
  root.style.setProperty('--bg', branding.background_color);

  // Update favicon if provided
  if (branding.favicon_url) {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      favicon.href = branding.favicon_url;
    }
  }

  // Update page title with company name
  if (branding.company_name) {
    document.title = `${branding.company_name} - Message Archive`;
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subdomain, setSubdomain] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenantConfig = async () => {
      const detectedSubdomain = getSubdomainFromHost();
      setSubdomain(detectedSubdomain);

      if (!detectedSubdomain) {
        // No tenant subdomain - use defaults
        setConfig({ tenant: null, branding: DEFAULT_BRANDING });
        applyBranding(DEFAULT_BRANDING);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE}/tenant/config?subdomain=${encodeURIComponent(detectedSubdomain)}`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.error && !data.branding) {
          setError(data.error);
          setConfig({ tenant: null, branding: DEFAULT_BRANDING });
          applyBranding(DEFAULT_BRANDING);
        } else {
          setConfig({
            tenant: data.tenant,
            branding: data.branding || DEFAULT_BRANDING,
          });
          applyBranding(data.branding || DEFAULT_BRANDING);
        }
      } catch (err) {
        console.error('Failed to fetch tenant config:', err);
        setError('Failed to load tenant configuration');
        setConfig({ tenant: null, branding: DEFAULT_BRANDING });
        applyBranding(DEFAULT_BRANDING);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantConfig();
  }, []);

  return (
    <TenantContext.Provider value={{ config, loading, error, subdomain }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
